import { getMapLegendRegistry, renderMapLegend } from './map-legend-registry';
import { createLayerGroupControl, syncLayerGroupControl } from '../modules/layer-group';

const URL_KEYS = {
    noiseLdwn: 'noise-ldwn',
    noiseLn: 'noise-ln',
    noiseMonitoring: 'noise-monitoring'
};

export const NOISE_INTERACTIVE_LAYER_IDS = [
    'lyr-noiseLdwn-fill',
    'lyr-noiseLdwn-line',
    'lyr-noiseLn-fill',
    'lyr-noiseLn-line',
    'lyr-noiseMonitoring-points'
];

const state = {
    noiseLdwnDesired: false,
    noiseLdwnActive: false,
    noiseLnDesired: false,
    noiseLnActive: false,
    noiseMonitoringDesired: false,
    noiseMonitoringActive: false
};

const controllers = {};
const metadata = {};
const stateListeners = new Set();
let mapBound = null;
let panelObserver = null;
let noiseGroupControl = null;
let noiseGroupOpen = false;
const CONTOUR_LEGEND_KEY = 'noise-levels';
const CONTOUR_LEGEND_OWNERS = ['noiseLdwn', 'noiseLn'];
const CONTOUR_LEGEND_DEFINITION = {
    title: 'Poziom hałasu na mapie',
    items: [
        { label: 'poniżej 55 decybeli', color: '#22c55e' },
        { label: '55–59 decybeli', color: '#eab308' },
        { label: '60–64 decybeli', color: '#f97316' },
        { label: '65–69 decybeli', color: '#ef4444' },
        { label: '70–74 decybeli', color: '#a855f7' },
        { label: 'od 75 decybeli', color: '#581c87' },
    ],
    note: 'Kliknij kolorowy obszar, aby poznać szczegóły.',
};
const MONITORING_LEGEND_DEFINITION = {
    title: 'Punkty pomiarowe hałasu',
    items: [
        { label: 'Niższy poziom', color: '#16856b', shape: 'dot' },
        { label: 'Umiarkowany', color: '#d49a20', shape: 'dot' },
        { label: 'Wysoki', color: '#e0642b', shape: 'dot' },
        { label: 'Bardzo wysoki', color: '#c63737', shape: 'dot' },
        { label: 'Brak wyniku', color: '#64748b', shape: 'dot' },
    ],
    note: 'Kliknij punkt, aby sprawdzić wskaźnik i okres pomiaru.',
};

function syncNoiseGroup() {
    if (!noiseGroupControl) return;
    syncLayerGroupControl(noiseGroupControl, {
        opened: noiseGroupOpen,
        selectedCount: Object.keys(URL_KEYS).filter(key => state[key + 'Desired']).length,
    });
}

function notifyStateChange() {
    syncNoiseGroup();
    stateListeners.forEach(function(listener) {
        listener(Object.assign({}, state));
    });
}

function readInitialUrlState() {
    if (typeof window === 'undefined' || !window.location) return;
    const params = new URLSearchParams(window.location.search || '');
    Object.keys(URL_KEYS).forEach(function(key) {
        const raw = params.get(URL_KEYS[key]);
        const enabled = raw === '1' || raw === 'true';
        state[key + 'Desired'] = enabled;
    });
}

function writeUrlState() {
    if (typeof window === 'undefined' || !window.location || !window.history) return;
    const url = new URL(window.location.href);
    Object.keys(URL_KEYS).forEach(function(key) {
        if (state[key + 'Desired']) {
            url.searchParams.set(URL_KEYS[key], '1');
        } else {
            url.searchParams.delete(URL_KEYS[key]);
        }
    });
    window.history.pushState(window.history.state, '', url.toString());
}

readInitialUrlState();

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function provenanceLabel(value) {
    const provenance = value && value.provenance ? value.provenance : null;
    if (!provenance) return '';
    const rawSource = provenance.sourceName || provenance.provider || 'źródło oficjalne';
    const source = String(rawSource).toLowerCase() === 'european environment agency'
        ? 'Europejska Agencja Środowiska'
        : rawSource;
    const period = provenance.referenceYear || provenance.dataAsOf || '';
    return 'Źródło: ' + source + (period ? ' · dane ' + period : '');
}

function infoHtml(key) {
    const value = metadata[key] || {};
    const provenance = value.provenance || null;
    const sourceUrl = provenance && provenance.sourceUrl ? provenance.sourceUrl : '';
    const label = provenanceLabel(value);
    let description = '';

    if (key === 'noiseLdwn') {
        description = 'Długookresowy poziom hałasu dla całej doby. Kliknij kolorowy obszar na mapie, aby poznać szczegóły.';
    } else if (key === 'noiseLn') {
        description = 'Długookresowy poziom hałasu w nocy. Kliknij kolorowy obszar na mapie, aby poznać szczegóły.';
    } else {
        description = value.representation === 'area_median'
            ? 'Przy tym przybliżeniu każdy większy punkt podsumowuje pomiary z części mapy. Etykieta pokazuje medianę, czyli wartość środkową najnowszych dostępnych wyników.'
            : 'Kolor i etykieta pokazują ostatni dostępny wynik z danego punktu pomiarowego. Kliknij punkt, aby sprawdzić okres i rodzaj pomiaru.';
    }

    const coverageText = value.coverage === false && key !== 'noiseMonitoring'
        ? '<div class="nm-layer-info__empty">Brak strategicznej mapy hałasu dla tego obszaru.</div>'
        : '';
    const sourceText = label
        ? (sourceUrl
            ? '<div class="nm-external-links"><a class="ms-link nm-ext-link" href="' + escapeHtml(sourceUrl)
                + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(label) + ' ↗</a></div>'
            : '<div class="nm-layer-info__source">' + escapeHtml(label) + '</div>')
        : '';

    const legend = key === 'noiseMonitoring'
        ? '<p>Kolor punktu podpowiada, czy ostatni wynik był niższy, umiarkowany, wysoki lub bardzo wysoki. To nie jest ocena zgodności z przepisami.</p>'
        : '';
    return '<p>' + escapeHtml(description) + '</p>' + legend + coverageText + sourceText
        + '<p>Zaznaczenie oznacza wybór warstwy, a kolor przycisku oznacza jej wyświetlanie. Możesz zmienić wybór także poza zakresem przybliżenia warstwy.</p>';
}

function syncButton(key) {
    if (typeof document === 'undefined') return;
    const button = document.querySelector('[data-noise-layer="' + key + '"]');
    if (!button) return;
    const controller = controllers[key];
    const map = controller && controller.getMap ? controller.getMap() : null;
    const zoom = map ? Math.round(map.getZoom() * 10) / 10 : 0;
    const minZoom = controller ? controller.minZoom : 0;
    button.classList.toggle('is-active', !!state[key + 'Active']);
    button.classList.toggle('is-desired', !!state[key + 'Desired']);
    button.classList.toggle('is-disabled', zoom < minZoom);
    button.setAttribute('aria-checked', state[key + 'Desired'] ? 'true' : 'false');
}

function syncNoiseLegends() {
    const controller = controllers.noiseLdwn || controllers.noiseLn || controllers.noiseMonitoring;
    const map = controller && controller.getMap ? controller.getMap() : null;
    const registry = getMapLegendRegistry(map);
    if (!registry) return;
    CONTOUR_LEGEND_OWNERS.forEach(function(owner) {
        registry.setVisible(CONTOUR_LEGEND_KEY, owner, state[owner + 'Active'], {
            className: 'ms-map-legend--noise',
            ariaLabel: 'Legenda poziomu hałasu na mapie',
            render: function(element) { renderMapLegend(element, CONTOUR_LEGEND_DEFINITION); },
        });
    });
    registry.setVisible('noise-monitoring', 'noiseMonitoring', state.noiseMonitoringActive, {
        className: 'ms-map-legend--noise-monitoring',
        ariaLabel: 'Legenda punktów pomiarowych hałasu',
        render: function(element) { renderMapLegend(element, MONITORING_LEGEND_DEFINITION); },
    });
}

function refreshInfo(key) {
    if (typeof document === 'undefined') return;
    const info = document.querySelector('[data-noise-info="' + key + '"]');
    if (info) info.innerHTML = infoHtml(key);
}

function syncAll() {
    Object.keys(controllers).forEach(function(key) {
        const controller = controllers[key];
        if (controller && typeof controller.syncVisibility === 'function') {
            const wasActive = controller.isActive();
            controller.syncVisibility();
            if (wasActive && controller.isActive()) controller.load();
        }
        syncButton(key);
    });
    syncNoiseLegends();
}

function bindMap(map) {
    if (!map || mapBound === map || !map.on) return;
    mapBound = map;
    map.on('moveend', syncAll);
    map.on('style.load', syncAll);
    map.on('movestart', function() {
        Object.values(controllers).forEach(function(controller) { controller.abortPending(); });
    });
    setTimeout(syncAll, 0);
}

function toggle(key) {
    const controller = controllers[key];
    if (!controller || !controller.getMap || !controller.getMap()) return false;
    const desired = !state[key + 'Desired'];
    state[key + 'Desired'] = desired;
    state[key + 'Active'] = desired && controller.canActivate();
    if (desired && controller.canActivate()) {
        controller.setActive(true);
        controller.load();
    } else {
        controller.setActive(false);
        controller.clear();
    }
    writeUrlState();
    syncButton(key);
    syncNoiseLegends();
    notifyStateChange();
    return true;
}

function createLayerItem(key, label, ariaLabel) {
    const item = document.createElement('div');
    item.className = 'nm-layer-item';

    const row = document.createElement('div');
    row.className = 'nm-layer-item-row';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'nm-layer-toggle nm-layer-toggle--checkbox nm-layer-toggle--noise';
    button.setAttribute('role', 'checkbox');
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('data-noise-layer', key);
    button.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18c3-5 5 2 8-4s5 1 10-6"/><path d="M3 6h18"/></svg><span class="nm-layer-toggle-label">' + escapeHtml(label) + '</span><span class="nm-layer-loading" aria-hidden="true"></span>';
    button.addEventListener('click', function(event) {
        event.stopPropagation();
        toggle(key);
    });

    const infoTrigger = document.createElement('button');
    infoTrigger.type = 'button';
    infoTrigger.className = 'ms-button ms-button--icon nm-layer-info-trigger';
    infoTrigger.setAttribute('aria-label', 'Pokaż informacje o źródle warstwy ' + label);
    infoTrigger.innerHTML = '<span class="nm-layer-info-trigger__icon">i</span>';

    const info = document.createElement('div');
    info.className = 'nm-layer-info';
    info.setAttribute('data-noise-info', key);
    info.innerHTML = infoHtml(key);

    infoTrigger.addEventListener('click', function(event) {
        event.preventDefault();
        event.stopPropagation();
        const visible = info.classList.toggle('is-visible');
        infoTrigger.classList.toggle('is-active', visible);
    });

    row.appendChild(button);
    row.appendChild(infoTrigger);
    item.appendChild(row);
    item.appendChild(info);
    return item;
}

function injectPanel() {
    if (typeof document === 'undefined') return false;
    const container = document.querySelector('[data-layer-panel-content]');
    if (!container || container.querySelector('[data-noise-layer-group]')) return !!container;

    noiseGroupControl = createLayerGroupControl({
        key: 'noise',
        label: 'Hałas środowiskowy',
        description: 'Strategiczne mapy hałasu i punkty pomiarowe',
        svgHtml: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16c2-4 4-4 6 0s4 4 6 0 4-4 4-4"/><path d="M4 8h16"/></svg>',
    });
    const { root: group, trigger, content } = noiseGroupControl;
    group.setAttribute('data-noise-layer-group', '1');
    content.appendChild(createLayerItem('noiseLdwn', 'Cała doba — strategiczny hałas (LDWN)', 'Przełącz strategiczną mapę hałasu dla całej doby'));
    content.appendChild(createLayerItem('noiseLn', 'Noc — strategiczny hałas (LN)', 'Przełącz strategiczną mapę hałasu dla nocy'));
    content.appendChild(createLayerItem('noiseMonitoring', 'Punkty pomiarowe Głównego Inspektoratu Ochrony Środowiska', 'Przełącz punkty pomiarowe hałasu Głównego Inspektoratu Ochrony Środowiska'));
    trigger.addEventListener('click', function(event) {
        event.stopPropagation();
        noiseGroupOpen = !noiseGroupOpen;
        syncNoiseGroup();
    });
    container.appendChild(group);
    syncNoiseGroup();
    Object.keys(URL_KEYS).forEach(syncButton);
    syncNoiseLegends();
    return true;
}

function ensurePanelInjection() {
    if (injectPanel() || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
    if (panelObserver) return;
    panelObserver = new MutationObserver(function() {
        if (injectPanel() && panelObserver) {
            panelObserver.disconnect();
            panelObserver = null;
        }
    });
    panelObserver.observe(document.documentElement, { childList: true, subtree: true });
}

export const noiseLayerRuntime = {
    registerController(key, controller) {
        controllers[key] = controller;
        if (controller && controller.getMap) bindMap(controller.getMap());
        ensurePanelInjection();
        syncButton(key);
    },
    getController(key) {
        return controllers[key] || null;
    },
    getUrlParams() {
        const params = {};
        Object.keys(URL_KEYS).forEach(function(key) {
            if (state[key + 'Desired']) params[URL_KEYS[key]] = '1';
        });
        return params;
    },
    getState() {
        return Object.assign({}, state);
    },
    getDesired(key) {
        return !!state[key + 'Desired'];
    },
    setDesired(key, value) {
        state[key + 'Desired'] = !!value;
        writeUrlState();
        syncButton(key);
        syncNoiseLegends();
        notifyStateChange();
    },
    getActive(key) {
        return !!state[key + 'Active'];
    },
    setActive(key, value) {
        state[key + 'Active'] = !!value;
        syncButton(key);
        syncNoiseLegends();
    },
    toggle,
    updateMetadata(key, value) {
        metadata[key] = value || {};
        refreshInfo(key);
    },
    getMetadata(key) {
        return metadata[key] || {};
    },
    getRenderedFeatures(key) {
        const controller = controllers[key];
        const map = controller && controller.getMap ? controller.getMap() : null;
        const layerId = key === 'noiseMonitoring'
            ? 'lyr-noiseMonitoring-points'
            : 'lyr-' + key + '-fill';
        if (!controller || !controller.isActive || !controller.isActive()
            || !map || !map.getLayer || !map.getLayer(layerId)
            || typeof controller.getRenderedFeatures !== 'function') {
            return [];
        }
        return controller.getRenderedFeatures();
    },
    subscribeState(listener) {
        if (typeof listener !== 'function') return function() {};
        stateListeners.add(listener);
        return function() { stateListeners.delete(listener); };
    },
    ensurePanelInjection,
    syncAll
};
